import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { Hash, Loader2 } from "lucide-react"
import { PostCard } from "@/components/posts/PostCard"
import { Sidebar } from "@/components/layout/Sidebar"
import { getPostsByTag, getPostsWithAuthors, enrichPostsWithTags } from "@/lib/db"
import type { PostWithAuthor } from "@/types"

export default function TagPage() {
  const { tag } = useParams<{ tag: string }>()
  const [posts, setPosts] = useState<PostWithAuthor[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadPosts() {
      if (!tag) return

      try {
        setLoading(true)
        const rawPosts = await getPostsByTag(tag)
        const postsData = await getPostsWithAuthors(rawPosts).then(enrichPostsWithTags)
        setPosts(postsData)
      } catch (err) {
        console.error("Failed to load posts:", err)
      } finally {
        setLoading(false)
      }
    }

    loadPosts()
  }, [tag])

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <Hash className="h-8 w-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{tag}</h1>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {posts.length} {posts.length === 1 ? "post" : "posts"} tagged with #{tag}
        </p>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="flex-1 space-y-6">
          {posts.length > 0 ? (
            posts.map(post => <PostCard key={post.id} post={post} />)
          ) : (
            <div className="py-12 text-center">
              <Hash className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No posts found</h3>
              <p className="mt-2 text-gray-500 dark:text-gray-400">No posts have been tagged with #{tag} yet.</p>
            </div>
          )}
        </div>
        <aside className="w-full lg:w-80">
          <Sidebar />
        </aside>
      </div>
    </div>
  )
}
